import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/shadcn/avatar';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/shadcn/dialog';
import { useInitials } from '@/hooks/use-initials';
import { useTranslations } from '@/hooks/useTranslations';
import { type User } from '@/types';
import { Link } from '@inertiajs/react';
import { BriefcaseBusiness, Building2, Mail, Phone, Settings, UserRound } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { route } from 'ziggy-js';

interface UserProfileCardProps {
    user: User;
    children: ReactNode;
}

function ProfileRow({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
    return (
        <div className="grid grid-cols-[110px_1fr] items-center gap-2 py-2.5 text-sm">
            <div className="text-muted-foreground flex items-center gap-2">
                {icon}
                {label}
            </div>
            <div className="min-w-0 truncate font-medium">{value}</div>
        </div>
    );
}

export function UserProfileCard({ user, children }: UserProfileCardProps) {
    const { t } = useTranslations();
    const getInitials = useInitials();
    const [open, setOpen] = useState(false);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent className="max-w-md gap-0 overflow-hidden p-0">
                <DialogTitle className="sr-only">{t('navigation.profile_card.title', 'Your profile')}</DialogTitle>
                <DialogDescription className="sr-only">{t('navigation.profile_card.description', 'Your account details')}</DialogDescription>

                <div className="flex items-center gap-4 border-b p-6">
                    <Avatar className="size-16 shrink-0 overflow-hidden rounded-full">
                        <AvatarImage src={user.avatar} alt={user.first_name} />
                        <AvatarFallback className="bg-muted text-muted-foreground rounded-full text-lg">
                            {getInitials(`${user.first_name} ${user.last_name}`)}
                        </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                            <span className="truncate text-lg leading-tight font-semibold">
                                {user.first_name} {user.last_name}
                            </span>
                            {user.azure_id && (
                                <span
                                    aria-label="Microsoft 365"
                                    title={t('employees.show.m365_badge', 'Microsoft 365 user')}
                                    className="inline-flex shrink-0"
                                >
                                    <svg width="13" height="13" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
                                        <path fill="#F25022" d="M1 1h10v10H1z" />
                                        <path fill="#7FBA00" d="M12 1h10v10H12z" />
                                        <path fill="#00A4EF" d="M1 12h10v10H1z" />
                                        <path fill="#FFB900" d="M12 12h10v10H12z" />
                                    </svg>
                                </span>
                            )}
                            {user.role && (
                                <Badge variant="secondary" className="font-normal">
                                    {user.role}
                                </Badge>
                            )}
                        </div>
                    </div>
                </div>

                <div className="space-y-4 p-6">
                    <div>
                        <h3 className="text-muted-foreground mb-1 text-[11px] font-semibold tracking-wider uppercase">
                            {t('navigation.profile_card.details', 'Details')}
                        </h3>
                        <div className="divide-y">
                            <ProfileRow
                                icon={<BriefcaseBusiness className="size-4" />}
                                label={t('employees.show.sidebar.position', 'Position')}
                                value={user.position || t('employees.show.sidebar.no_position', 'No position set')}
                            />
                            <ProfileRow
                                icon={<Building2 className="size-4" />}
                                label={t('employees.show.sidebar.department', 'Department')}
                                value={user.department || t('employees.show.sidebar.no_department', 'No department set')}
                            />
                        </div>
                    </div>

                    <div>
                        <h3 className="text-muted-foreground mb-1 text-[11px] font-semibold tracking-wider uppercase">
                            {t('employees.show.sidebar.contact_information', 'Contact Information')}
                        </h3>
                        <div className="divide-y">
                            <ProfileRow
                                icon={<Mail className="size-4" />}
                                label={t('employees.show.sidebar.email', 'Email')}
                                value={
                                    user.email ? (
                                        <a href={`mailto:${user.email}`} className="text-primary hover:underline">
                                            {user.email}
                                        </a>
                                    ) : (
                                        t('employees.show.sidebar.no_email', 'No email provided')
                                    )
                                }
                            />
                            <ProfileRow
                                icon={<Phone className="size-4" />}
                                label={t('employees.show.sidebar.phone', 'Phone')}
                                value={
                                    user.phone ? (
                                        <a href={`tel:${user.phone}`} className="text-primary hover:underline">
                                            {user.phone}
                                        </a>
                                    ) : (
                                        t('employees.show.sidebar.no_phone', 'No phone provided')
                                    )
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-muted/30 flex items-center gap-2 border-t p-4">
                    <Button asChild variant="secondary" className="flex-1">
                        <Link href={route('profile.show')} onClick={() => setOpen(false)}>
                            <UserRound className="size-4" />
                            {t('navigation.profile_card.view_profile', 'View profile')}
                        </Link>
                    </Button>
                    <Button asChild variant="outline" className="flex-1">
                        <Link href={route('profile.edit')} onClick={() => setOpen(false)}>
                            <Settings className="size-4" />
                            {t('navigation.settings', 'Settings')}
                        </Link>
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
